// Platform Settings API
import api, { type AxiosResponse } from './client';
import type {
  PlatformSettings,
  PlatformSettingsUpdate,
  PublicBranding,
  CountryDetectionResponse,
  MergeCompaniesResponse,
  DateAdjustmentResponse,
  DateAdjustmentLog,
  ClientFeatureFlags,
  DeliveryGrainSettingsResponse,
  DeliveryGrainSetting,
  CustomerDeliveryGrain,
  ForecastScopeSettingsResponse,
  ForecastScope,
  CustomerForecastScope,
} from './types';

/** CR-4751: the nightly Cached Performance capture (Platform Admin, Helix only). */
export interface DashboardSnapshotSchedule {
  /** False when this install has no schedule row yet — saving creates it. */
  exists: boolean;
  enabled: boolean;
  /** UTC; null when the stored crontab is not a single daily time. */
  hour: number | null;
  minute: number | null;
  timezone: string;
  last_run_at: string | null;
  total_run_count: number;
}

export interface ArrearsSnapshotGroupRow {
  group_id: number;
  group_name: string;
  /** The group's effective timezone; the group schedule runs on this clock */
  timezone: string;
  /** ON = every member site freezes at the one group moment below */
  arrears_snapshot_group_schedule: boolean;
  arrears_snapshot_day: number;
  arrears_snapshot_hour: number;
}

export interface ArrearsSnapshotSiteRow {
  company_id: number;
  company_name: string;
  group_id: number | null;
  capture_arrears_snapshots: boolean;
  /** Site override: day of the month (site-local, 1-28) */
  arrears_snapshot_day: number;
  /** Site override: hour of that day (site-local, 0-23) */
  arrears_snapshot_hour: number;
}

/** FORECAST-FLUCTUATION: per-site weekly forecast copy schedule (Helix operators). */
export interface ForecastSnapshotSiteRow {
  company_id: number;
  company_name: string;
  capture_forecast_snapshots: boolean;
  /** Site-local weekday the copy is taken: 0 = Monday .. 6 = Sunday */
  forecast_snapshot_weekday: number;
  /** Site-local hour of that weekday (0-23) */
  forecast_snapshot_hour: number;
}

export const platformSettingsAPI = {
  // FORECAST-FLUCTUATION: weekly forecast copy schedules (Helix operators)
  getForecastSnapshotConfig: (): Promise<AxiosResponse<{ sites: ForecastSnapshotSiteRow[] }>> =>
    api.get('/core/settings/forecast-snapshot-config/'),

  updateForecastSnapshotSite: (
    data: Partial<ForecastSnapshotSiteRow> & { company_id: number },
  ): Promise<AxiosResponse<ForecastSnapshotSiteRow>> =>
    api.patch('/core/settings/forecast-snapshot-config/', data),

  // CR-4751: when the nightly Cached Performance snapshot is captured (UTC)
  getDashboardSnapshotSchedule: (): Promise<AxiosResponse<DashboardSnapshotSchedule>> =>
    api.get('/core/settings/dashboard-snapshot-schedule/'),

  updateDashboardSnapshotSchedule: (
    data: Partial<Pick<DashboardSnapshotSchedule, 'enabled' | 'hour' | 'minute'>>,
  ): Promise<AxiosResponse<DashboardSnapshotSchedule>> =>
    api.patch('/core/settings/dashboard-snapshot-schedule/', data),

  // METRIC-DEFINITIONS Q12/Q20: arrears snapshot schedules (Helix operators)
  getArrearsSnapshotConfig: (): Promise<AxiosResponse<{
    groups: ArrearsSnapshotGroupRow[]; sites: ArrearsSnapshotSiteRow[];
  }>> =>
    api.get('/core/settings/arrears-snapshot-config/'),

  updateArrearsSnapshotGroup: (
    data: Partial<ArrearsSnapshotGroupRow> & { group_id: number },
  ): Promise<AxiosResponse<Partial<ArrearsSnapshotGroupRow>>> =>
    api.patch('/core/settings/arrears-snapshot-config/', data),

  updateArrearsSnapshotSite: (
    data: Partial<ArrearsSnapshotSiteRow> & { company_id: number },
  ): Promise<AxiosResponse<ArrearsSnapshotSiteRow>> =>
    api.patch('/core/settings/arrears-snapshot-config/', data),

  // Get platform settings (requires auth)
  getSettings: (): Promise<AxiosResponse<PlatformSettings>> =>
    api.get('/core/settings/platform/'),

  // Update platform settings
  updateSettings: (data: PlatformSettingsUpdate): Promise<AxiosResponse<{ message: string; data: PlatformSettings }>> =>
    api.put('/core/settings/platform/', data),

  // Partial update of platform settings
  patchSettings: (data: Partial<PlatformSettingsUpdate>): Promise<AxiosResponse<{ message: string; data: PlatformSettings }>> =>
    api.patch('/core/settings/platform/', data),

  /**
   * CR-6243: upload one branding image (logo / login background).
   *
   * This MUST NOT go through ``patchSettings``. The shared axios instance sets
   * a default ``Content-Type: application/json``, and axios 1.x reacts to that
   * by serialising a FormData body to JSON rather than sending multipart
   * (see its transformRequest: ``hasJSONContentType ? JSON.stringify(
   * formDataToJSON(data)) : data``). A File has no enumerable properties, so
   * the file arrived at the server as ``{}`` — which failed validation on the
   * fields that exist and was silently dropped on the ones that did not.
   * Naming the multipart type here is what every other upload in this codebase
   * already does.
   *
   * @param formData - one entry, keyed by the backend field name
   *                   (company_logo / company_dark_logo / header_logo / login_image)
   * @returns the full updated settings, so the caller can confirm the new URL
   *          landed instead of trusting the status code
   */
  uploadBrandingImage: (formData: FormData): Promise<AxiosResponse<{ message: string; data: PlatformSettings }>> =>
    api.patch('/core/settings/platform/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // CR-5892: client-editable (instance-wide) feature flags (Helix or company admin).
  // Lets Admin Setup manage "Allow Supplier Decline" without the Helix-only
  // platform settings endpoint. Whitelisted keys are stored on the shared
  // PlatformSettings so the change reaches the supplier portal's decline gate.
  getClientFeatureFlags: (): Promise<AxiosResponse<ClientFeatureFlags>> =>
    api.get('/core/settings/client-feature-flags/'),

  patchClientFeatureFlags: (data: Partial<ClientFeatureFlags>): Promise<AxiosResponse<ClientFeatureFlags>> =>
    api.patch('/core/settings/client-feature-flags/', data),

  // Get public branding (no auth required) - used for login page
  // Add timestamp to bypass browser cache
  getPublicBranding: (): Promise<AxiosResponse<PublicBranding>> =>
    api.get('/core/settings/branding/', { params: { _t: Date.now() } }),

  // Get company-specific branding (requires auth) - used after login
  getCompanyBranding: (): Promise<AxiosResponse<PublicBranding>> =>
    api.get('/core/settings/company-branding/', { params: { _t: Date.now() } }),

  // Update company-specific branding
  patchCompanyBranding: (data: Partial<PublicBranding>): Promise<AxiosResponse<{ message: string; data: PublicBranding }>> =>
    api.patch('/core/settings/company-branding/', data),

  // Get platform display settings (any authenticated user — tab visibility, feature flags).
  // Also carries the per-scope delivery grain keys (see DisplaySettingsDeliveryGrain in
  // types/common.ts) — read off the raw payload by MenuSettingsContext.
  // CR-6385: concessions_enabled / concession_enabled_customer_ids are resolved
  // per user by the endpoint rather than stored on PlatformSettings, so they sit
  // alongside the Pick rather than inside it.
  getDisplaySettings: (): Promise<AxiosResponse<Pick<PlatformSettings, 'dashboard_tabs' | 'supplier_dashboard_tabs' | 'purchasing_tabs' | 'quality_tabs' | 'menu_visibility' | 'main_menu_type' | 'feature_flags' | 'supplier_card_fields' | 'data_import_tiles' | 'supplier_default_landing_path' | 'quality_measure_ppk' | 'enable_8d_scoring' | 'ncr_template'> & { concessions_enabled?: boolean; concession_enabled_customer_ids?: number[]; ncr_escape_root_cause_enabled?: boolean; ncr_escape_root_cause_customer_ids?: number[] }>> =>
    api.get('/core/settings/display/'),

  // Per-customer delivery grain (Helix-only): list + set one customer.
  getDeliveryGrains: (): Promise<AxiosResponse<DeliveryGrainSettingsResponse>> =>
    api.get('/core/settings/delivery-grain/'),
  updateDeliveryGrain: (companyId: number, grain: DeliveryGrainSetting): Promise<AxiosResponse<CustomerDeliveryGrain>> =>
    api.patch('/core/settings/delivery-grain/', { company_id: companyId, delivery_grain: grain }),

  // Per-customer forecast attribution scope (Helix-only): list + set one customer.
  getForecastScopes: (): Promise<AxiosResponse<ForecastScopeSettingsResponse>> =>
    api.get('/core/settings/forecast-scope/'),
  updateForecastScope: (companyId: number, scope: ForecastScope): Promise<AxiosResponse<CustomerForecastScope>> =>
    api.patch('/core/settings/forecast-scope/', { company_id: companyId, forecast_scope: scope }),

  // Detect country from IP (Feature 7)
  detectCountry: (): Promise<AxiosResponse<CountryDetectionResponse>> =>
    api.get('/core/settings/detect-country/'),

  // Merge companies (Feature 1)
  mergeCompanies: (targetId: number, sourceId: number): Promise<AxiosResponse<MergeCompaniesResponse>> =>
    api.post('/core/settings/merge-companies/', { target_id: targetId, source_id: sourceId }),

  // Adjust dates (Feature 6)
  adjustDates: (direction: 'plus' | 'minus'): Promise<AxiosResponse<DateAdjustmentResponse>> =>
    api.post('/core/settings/adjust-dates/', { direction }),

  // Get date adjustment logs (Feature 6)
  getDateAdjustmentLogs: (): Promise<AxiosResponse<DateAdjustmentLog[]>> =>
    api.get('/core/settings/date-adjustment-logs/'),

  // CR-6346: Price Book Sharing — the supplier roster backing the
  // "Custom (Selected Suppliers)" state, plus the configuration audit trail.
  getPriceBookSharingSuppliers: (): Promise<AxiosResponse<PriceBookSharingSuppliersResponse>> =>
    api.get('/core/settings/price-book-sharing/suppliers/'),

  updatePriceBookSharingSuppliers: (
    suppliers: PriceBookSharingSupplierUpdate[],
  ): Promise<AxiosResponse<{ message: string; updated: PriceBookSharingSupplierUpdate[] }>> =>
    api.patch('/core/settings/price-book-sharing/suppliers/', { suppliers }),

  getPriceBookSharingHistory: (
    limit = 20,
  ): Promise<AxiosResponse<PriceBookSharingHistoryResponse>> =>
    api.get('/core/settings/price-book-sharing/history/', { params: { limit } }),

  // Supplier-side: does this supplier have shared price book access? Drives the
  // Price Book tab in Sales Orders.
  getMyPriceBookAccess: (): Promise<AxiosResponse<MyPriceBookAccess>> =>
    api.get('/core/settings/price-book-sharing/my-access/'),
};

// ============================================================================
// CR-6346: Price Book Sharing
// ============================================================================

/** One supplier row in the Custom (Selected Suppliers) picker. */
export interface PriceBookSharingSupplier {
  supplier_id: number;
  supplier_name: string;
  supplier_code: string;
  price_book_shared: boolean;
}

export interface PriceBookSharingSuppliersResponse {
  sharing_flag: 'all_off' | 'all_on' | 'custom';
  count: number;
  results: PriceBookSharingSupplier[];
}

export interface PriceBookSharingSupplierUpdate {
  supplier_id: number;
  price_book_shared: boolean;
}

/** One field-level change within an audit entry. */
export interface PriceBookSharingHistoryEntry {
  field: string;
  old: string;
  new: string;
}

export interface PriceBookSharingHistoryRow {
  id: number;
  timestamp: string;
  user_name: string;
  action: string;
  entries: PriceBookSharingHistoryEntry[];
}

export interface PriceBookSharingHistoryResponse {
  count: number;
  results: PriceBookSharingHistoryRow[];
}

export interface MyPriceBookAccess {
  has_access: boolean;
  customer_count: number;
}

// ============================================================================
// CR-5905: Demo Data Roll-Forward Utility (Platform Admin only)
// ============================================================================

export interface DemoDateShiftTable {
  id: number;
  table_key: string;
  display_name: string;
  plus_count: number;
  minus_count: number;
  last_action: '' | 'plus' | 'minus';
  last_actor_name: string | null;
  last_action_at: string | null;
  last_affected_rows: number;
}

export interface DemoDateShiftResult {
  table_key: string;
  display: string;
  rows_affected: number;
}

// CR-6240: after a shift the backend flushes the live dashboard caches inline
// and queues the heavier snapshot/supplier-performance rebuild on a worker.
export type DemoDateSnapshotRecalcStatus =
  | 'queued'
  | 'skipped (no worker)'
  | 'skipped (queue unavailable)';

export interface DemoDateShiftRecalc {
  dashboard_caches_invalidated: number;
  snapshot_recalc: DemoDateSnapshotRecalcStatus;
  errors: string[];
}

export interface DemoDateShiftResponse {
  direction: 'plus' | 'minus';
  table_key: string | null;
  total_rows_affected: number;
  recalc?: DemoDateShiftRecalc;
  results: DemoDateShiftResult[];
}

// CR-7091: what became of the last post-shift trend rebuild.
export type DemoDateRebuildState = 'queued' | 'running' | 'success' | 'failed' | 'skipped';

export interface DemoDateRebuildRun {
  id: number;
  status: DemoDateRebuildState;
  /** Skip reason (the shift's status string) or the error a rebuild hit. */
  detail: string;
  requested_by_name: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface DemoDateRebuildStatus {
  last_run: DemoDateRebuildRun | null;
  /** Jobs waiting in the rebuild's queue; null when the queue can't be read. */
  jobs_waiting: number | null;
}

export const demoDateShiftAPI = {
  list: (): Promise<AxiosResponse<DemoDateShiftTable[]>> =>
    api.get('/core/admin/demo-date-shift/'),

  rebuildStatus: (): Promise<AxiosResponse<DemoDateRebuildStatus>> =>
    api.get('/core/admin/demo-date-shift/rebuild-status/'),

  shift: (
    direction: 'plus' | 'minus',
    tableKey?: string,
  ): Promise<AxiosResponse<DemoDateShiftResponse>> =>
    api.post('/core/admin/demo-date-shift/shift/', {
      direction,
      ...(tableKey ? { table_key: tableKey } : {}),
    }),
};
